import {memo, useEffect, useRef, useState} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {trackEvent} from '~/shared/services/analytics';
import {getLocalizationService} from '~/shared/services/localisationService';

import InitScreen from './screens/InitScreen';
import LoadingScreen from './screens/LoadingScreen';
import SuccessScreen from './screens/SuccessScreen';
import FailureScreen from './screens/FailureScreen';
import {successGradient} from './styled';

type ConvertStatus = 'init' | 'loading' | 'success' | 'failure';

/* The success screen is the one screen of the flow the design paints edge to edge, and it is shorter
   than the screens that lead to it - the sheet closes up around it once there is nothing left to
   confirm. */
const Root = styled.div<{$status: ConvertStatus}>`
  ${flexColumn}

  &&& {
    width: 100%;
    height: ${({$status}) => ($status === 'success' ? 467 : 528)}px;
    background-color: ${({theme}) => theme.colors.surface};
    ${({$status}) => $status === 'success' && successGradient}
  }

  ${media.minLargeMobile`
    &&& {
      width: 500px;
      height: auto;
      border-radius: 20px;
      overflow: hidden;
    }
  `}
`;

/* The design's close button is white with a border rather than the default grey fill. */
const CloseButton = styled(PieCloseModalButton)`
  &&& {
    background-color: ${({theme}) => theme.colors.actionWhite};
    border: 1px solid ${({theme}) => theme.colors.borderStrong};
  }
`;

type ConvertLoyaltyBalanceModalProps = {
  args: {
    currentBalance: number;
    convertBalance: () => Promise<void>;
    setCurrentBalance: (balance: number) => void;
  };
  closeModal: () => void;
};

const ConvertLoyaltyBalanceModal = ({
  args: {currentBalance, convertBalance, setCurrentBalance},
  closeModal,
}: ConvertLoyaltyBalanceModalProps) => {
  const [status, setStatus] = useState<ConvertStatus>('init');
  /* The success screen's confetti is painted outside this element, so it needs it to measure. */
  const rootRef = useRef<HTMLDivElement>(null);

  const {t} = getLocalizationService();

  const ACTION_BUTTON_TEXT_ON_INIT = t('tap_and_save_convert_to_credit');
  const ACTION_BUTTON_TEXT_ON_FAILURE = t('tap_and_save_try_again');
  const FAILURE_SCREEN_SUB_TITLE = t('tap_and_save_convert_error_subtitle');

  useEffect(() => {
    return () => {
      if (status === 'success') {
        setCurrentBalance(0);
      }
    };
  }, [setCurrentBalance, status]);

  const convertBalanceClick = async () => {
    if (status === 'loading') {
      return;
    }

    setStatus('loading');

    try {
      await convertBalance();
      setStatus('success');
    } catch {
      setStatus('failure');
      trackEvent('cashbackAddToCredit_failed', {
        cashbackBalance: currentBalance,
        subTitleErrorMessage: FAILURE_SCREEN_SUB_TITLE,
      });
    }
  };

  const submitConvertBalance = ({isSubmittedFromTryAgain}: {isSubmittedFromTryAgain: boolean}) => {
    trackEvent('cashbackAddToCredit_submit', {
      cashbackBalance: currentBalance,
      isSubmittedFromTryAgain,
    });
    convertBalanceClick();
  };

  const renderScreen = () => {
    if (status === 'loading') {
      return <LoadingScreen />;
    }

    if (status === 'success') {
      return <SuccessScreen convertedAmount={currentBalance} onClose={closeModal} dialogRef={rootRef} />;
    }

    if (status === 'failure') {
      return <FailureScreen onRetry={() => submitConvertBalance({isSubmittedFromTryAgain: true})} ctaText={ACTION_BUTTON_TEXT_ON_FAILURE} subTitle={FAILURE_SCREEN_SUB_TITLE} />;
    }

    return (
      <InitScreen
        ctaText={ACTION_BUTTON_TEXT_ON_INIT}
        currentBalance={currentBalance}
        onConvert={() => submitConvertBalance({isSubmittedFromTryAgain: false})}
        onCancel={closeModal}
      />
    );
  };

  return (
    <Root ref={rootRef} $status={status} data-test-id="convertLoyaltyBalanceModal" data-status={status}>
      {/* There is nothing to cancel once the request is out, so the only way out is to let it land. */}
      {status !== 'loading' && <CloseButton onClick={closeModal} hideOnMobile />}
      {renderScreen()}
    </Root>
  );
};

type StaticTypes = {
  mobileBottomSheet?: boolean;
  absoluteBottomSheetBadge?: boolean;
  hideCloseButton?: boolean;
};

const EnhancedConvertLoyaltyBalanceModal: React.FC<ConvertLoyaltyBalanceModalProps> & StaticTypes = memo(ConvertLoyaltyBalanceModal);

EnhancedConvertLoyaltyBalanceModal.mobileBottomSheet = true;
// Lets the gradient run to the very top of the sheet, with the drag handle floating over it.
EnhancedConvertLoyaltyBalanceModal.absoluteBottomSheetBadge = true;
// Rendered by this component instead, so it can sit above the header decoration.
EnhancedConvertLoyaltyBalanceModal.hideCloseButton = true;

export default attachModal('convertLoyaltyBalanceModal', EnhancedConvertLoyaltyBalanceModal);
